import React from "react";
import { Link } from "react-router-dom";
import PolicyLayout, { PolicySection } from "./PolicyLayout";

export function DataCollection() {
  return (
    <PolicyLayout
      title="Personal Information Collection Statement"
      intro="This statement applies when mefurvexo asks you to provide personal information through checkout, customer support, a return, or another store form. It supplements the Privacy Policy."
      contactSubject="the collection, access, or correction of personal information"
    >
      <PolicySection title="Purpose of collection">
        <p>Information submitted may be used to:</p>
        <ul>
          <li>create, verify, accept, fulfil, deliver, and record an order;</li>
          <li>process payment, refunds, exchanges, warranty requests, and customer support;</li>
          <li>communicate service notices and respond to an enquiry;</li>
          <li>detect fraud, protect customers, secure the website, and resolve disputes;</li>
          <li>meet accounting, tax, customs, recordkeeping, and other legal obligations; and</li>
          <li>improve store operations using data proportionate to that purpose.</li>
        </ul>
      </PolicySection>

      <PolicySection title="Required and optional fields">
        <p>
          Supply is generally voluntary. A field marked required is needed for the
          transaction or request described beside the form. Without it, we may not
          be able to process payment, deliver an order, verify a request, or respond.
          Optional fields help with delivery or communication but may be left blank.
        </p>
      </PolicySection>

      <PolicySection title="Possible recipients">
        <p>
          Necessary information may be transferred to payment and fraud-screening
          providers; hosting, security, and support providers; fulfilment, postal,
          courier, and customs providers; professional advisers; and authorities or
          courts where required or permitted by law. These recipients may operate
          outside Hong Kong or your country.
        </p>
      </PolicySection>

      <PolicySection title="Direct marketing">
        <p>
          If a separate marketing choice is offered, we may propose using your name,
          email address, and shopping interests to market mefurvexo jewellery,
          accessories, and store offers. We will provide the notices and obtain the
          indication of consent required by applicable law first. We will not provide
          personal information to another person for that person&apos;s direct marketing
          without the separate written consent required by law. Opt-out is free.
        </p>
      </PolicySection>

      <PolicySection title="Access and correction">
        <p>
          You may request access to and correction of personal information held by
          us, subject to applicable law. Address a written request to the Privacy
          Contact at the email or postal address below. We may verify identity and
          may charge only a fee permitted by law and explained in advance. More
          information appears in the <Link to="/privacy">Privacy Policy</Link>.
        </p>
      </PolicySection>
    </PolicyLayout>
  );
}

export function Payments() {
  return (
    <PolicyLayout
      title="Payment & Pricing Policy"
      intro="This policy explains how prices, payment authorisations, taxes, and refunds are handled in the mefurvexo online store."
      contactSubject="payment, pricing, or billing"
    >
      <PolicySection title="1. Currency and displayed prices">
        <p>
          The store currently displays product prices and charges orders in euros
          (EUR) unless another currency is expressly shown. A card issuer may apply
          its own exchange rate or cross-border fee; those charges are not set by us.
          Prices may change prospectively, but a change does not alter an accepted
          order except where law allows correction of an obvious error.
        </p>
      </PolicySection>

      <PolicySection title="2. Taxes, shipping, and import charges">
        <p>
          Delivery charges and taxes collected by us are shown before payment where
          they can be calculated. Import duties and destination charges are handled
          as described in the <Link to="/shipping">Shipping &amp; Delivery Policy</Link>.
        </p>
      </PolicySection>

      <PolicySection title="3. Payment processing">
        <p>
          Available methods appear at checkout. Card details are entered on a hosted
          page operated by the payment provider shown there. That provider may apply
          security checks and its own privacy terms. We receive payment status and
          transaction references rather than intentionally storing complete card
          numbers on this website.
        </p>
      </PolicySection>

      <PolicySection title="4. Authorisation, capture, and failed payment">
        <p>
          Submitting payment authorises the order amount and applicable charges. An
          issuer may show a pending hold before the payment is captured. A failed or
          abandoned payment does not create an accepted order. A pending hold from a
          failed attempt is normally released by the issuer under its own timing.
        </p>
      </PolicySection>

      <PolicySection title="5. Pricing or listing errors">
        <p>
          If a price or description is clearly incorrect, we may pause acceptance
          and ask whether you want to continue at the correct price, or cancel and
          refund the order. We will not substitute a product or increase a completed
          charge without authorisation. Mandatory rights remain unaffected.
        </p>
      </PolicySection>

      <PolicySection title="6. Fraud review">
        <p>
          An order may be held while payment authority, identity, or delivery details
          are reviewed. We request only information reasonably needed for the review
          and handle it under the <Link to="/privacy">Privacy Policy</Link>. Never
          send a complete card number or security code by email.
        </p>
      </PolicySection>

      <PolicySection title="7. Refunds and payment disputes">
        <p>
          Approved refunds are sent to the original method where possible. Provider
          and bank posting times vary. If you do not recognise a charge or believe a
          payment is wrong, contact us promptly so it can be investigated. Nothing
          here prevents use of a lawful card-provider or consumer remedy.
        </p>
      </PolicySection>
    </PolicyLayout>
  );
}

export function Cancellations() {
  return (
    <PolicyLayout
      title="Order Changes & Cancellation Policy"
      intro="We begin processing orders after payment, so send any change or cancellation request as soon as possible."
      contactSubject="an order change or cancellation"
    >
      <PolicySection title="Before dispatch">
        <p>
          Email us with the order number and requested change. We will try to change
          an address, selected option, or quantity, or cancel the order, but cannot
          guarantee it once processing has begun. We will never request payment-card
          credentials by email to make a change.
        </p>
      </PolicySection>

      <PolicySection title="After dispatch">
        <p>
          Once a parcel is with the carrier, cancellation and address changes may no
          longer be possible. The order may need to be delivered and returned under
          the <Link to="/returns">Returns &amp; Refunds Policy</Link>. Refusing delivery
          can cause delay or carrier charges, so contact us before doing so.
        </p>
      </PolicySection>

      <PolicySection title="Custom and made-to-order items">
        <p>
          Personalised, engraved, resized, or made-to-order work may become
          non-cancellable once production starts if this was clearly disclosed
          before purchase and is permitted by law. Fault and misdescription remedies
          continue to apply.
        </p>
      </PolicySection>

      <PolicySection title="Cancellation by mefurvexo">
        <p>
          We may cancel before acceptance for unavailable stock, payment failure,
          suspected fraud, an obvious listing error, a delivery restriction, or a
          legal requirement. We will explain where appropriate and return any captured
          amount to the original payment method. We will not replace an item without
          your agreement.
        </p>
      </PolicySection>

      <PolicySection title="Refund timing">
        <p>
          A cancellation refund is initiated promptly after confirmation. Your bank
          or payment provider controls when it appears. If only an authorisation hold
          was placed, the issuer may release it rather than show a separate refund.
        </p>
      </PolicySection>
    </PolicyLayout>
  );
}

export function Warranty() {
  return (
    <PolicyLayout
      title="Warranty & Faulty Goods Policy"
      intro="Products should match their description, be of acceptable quality, and be reasonably fit for any purpose we expressly agree to. This policy explains how we assess a concern without limiting statutory rights."
      contactSubject="a fault, damage, or incorrect product"
    >
      <PolicySection title="What is covered">
        <p>
          Contact us if an item arrives damaged, is different from what was ordered,
          has a material or manufacturing fault, or does not meet a non-excludable
          legal guarantee. A concern can be covered even after a change-of-mind
          return window ends.
        </p>
      </PolicySection>

      <PolicySection title="What is not normally a product fault">
        <ul>
          <li>ordinary wear, tarnish, plating wear, scratches, or gradual finish changes consistent with the product description;</li>
          <li>damage from impact, pulling, snagging, bending, crushing, chemicals, heat, moisture, incorrect storage, or unsuitable cleaning;</li>
          <li>loss of an item or component after delivery;</li>
          <li>natural variation disclosed for gemstones, pearls, texture, colour, or hand-finishing; or</li>
          <li>alteration or repair by someone not authorised by us where it caused the issue.</li>
        </ul>
        <p>
          An exclusion applies only where it is relevant to the cause and permitted
          by law. Review the <Link to="/product-care">Product Care &amp; Safety Guide</Link>.
        </p>
      </PolicySection>

      <PolicySection title="Making a claim">
        <p>
          Stop using an item that may be unsafe. Email the order number, a clear
          description of the issue, when it appeared, and photographs or video where
          helpful. Proof of purchase is required, but we may accept other reasonable
          evidence if the original confirmation is unavailable.
        </p>
      </PolicySection>

      <PolicySection title="Assessment and remedies">
        <p>
          We may request return of the item for a fair assessment. Depending on the
          nature of the issue and applicable law, a remedy may be repair, replacement,
          price reduction, or refund. A major or unsafe failure may entitle you to a
          different remedy than a minor issue. We will not require acceptance of
          store credit where a cash refund is legally required.
        </p>
      </PolicySection>

      <PolicySection title="Costs and replacement items">
        <p>
          Where the claim is valid, we cover reasonable assessment and return costs
          as required by law. A replacement is covered for the remainder of any
          applicable original coverage or a longer period required by law. This
          policy is in addition to, not a substitute for, mandatory consumer rights.
        </p>
      </PolicySection>
    </PolicyLayout>
  );
}

export function ProductCare() {
  return (
    <PolicyLayout
      title="Product Care & Safety Guide"
      intro="Use the material and care information on the product page together with this general jewellery guide. Product-specific instructions take priority."
      contactSubject="materials, care, or product safety"
    >
      <PolicySection title="Materials and natural variation">
        <p>
          Check each product page for the stated base metal, finish, stone, pearl,
          and measurements. Natural and hand-finished components can vary in colour,
          pattern, shape, and surface. A product is not represented as solid gold,
          hypoallergenic, waterproof, ethically certified, or suitable for a
          particular medical need unless that claim appears expressly in its details.
        </p>
      </PolicySection>

      <PolicySection title="Everyday care">
        <ul>
          <li>Apply perfume, cosmetics, sunscreen, and hair products before putting jewellery on.</li>
          <li>Remove jewellery before bathing, swimming, exercise, sleep, cleaning, or manual work.</li>
          <li>Avoid chlorine, salt water, household chemicals, hand sanitiser, excessive heat, impact, pulling, and abrasion.</li>
          <li>Wipe gently with a clean, dry, soft cloth after wear; use only a cleaner confirmed suitable for the stated material.</li>
          <li>Store pieces dry and separately in a closed pouch or box to reduce tangling, scratching, and tarnish.</li>
        </ul>
      </PolicySection>

      <PolicySection title="Plating, silver, stones, and pearls">
        <p>
          Plated finishes can gradually wear, especially on high-contact areas; this
          is not the same as solid precious metal. Sterling silver can naturally
          tarnish. Stones and pearls may be sensitive to chemicals, ultrasonic
          cleaning, heat, or sudden temperature change. Do not assume a setting is
          suitable for swimming or impact merely because the metal tolerates water.
        </p>
      </PolicySection>

      <PolicySection title="Fit, skin sensitivity, and children">
        <p>
          Choose a size that does not restrict circulation or catch easily. Stop use
          and seek appropriate medical advice if irritation occurs. Material
          sensitivities differ between people. Jewellery and packaging can include
          small parts, cords, pins, or magnets and must be kept away from babies,
          young children, pets, and anyone likely to swallow or misuse them.
        </p>
      </PolicySection>

      <PolicySection title="Inspection and safety reports">
        <p>
          Check clasps, posts, chains, and settings before wear. Stop using a loose,
          sharp, damaged, or unusually discoloured item. For a possible safety issue,
          email us with the order number and photographs. Safety notices or recalls,
          if ever needed, will identify the affected product and remedy without
          reducing legal rights.
        </p>
      </PolicySection>
    </PolicyLayout>
  );
}

export function Promotions() {
  return (
    <PolicyLayout
      title="Promotions Policy"
      intro="These default terms apply to discount codes, sales, free gifts, and other mefurvexo offers unless the offer clearly states different terms."
      contactSubject="a promotion or discount"
    >
      <PolicySection title="Offer period and eligibility">
        <p>
          An offer begins and ends at the time and time zone stated with it and
          applies only to eligible products, destinations, and customers. A code must
          be entered before payment and cannot normally be added retrospectively.
          One code may be used per order unless combination is expressly allowed.
        </p>
      </PolicySection>

      <PolicySection title="Exclusions and availability">
        <p>
          Delivery, duties, taxes, gift cards, prior purchases, and specified products
          may be excluded if disclosed with the offer. Products remain subject to
          stock. A promotion does not reserve an item in the cart.
        </p>
      </PolicySection>

      <PolicySection title="Free gifts and bundles">
        <p>
          A free gift is subject to stock and any disclosed qualifying spend. No cash
          alternative is offered unless required by law. If a return makes the order
          ineligible, return the unused gift or the lawful refund may be adjusted by
          its disclosed value. Bundle refunds reflect the price actually allocated to
          each returned item.
        </p>
      </PolicySection>

      <PolicySection title="Fair use and errors">
        <p>
          We may reject duplicate, automated, deceptive, or resale-driven use that
          violates disclosed terms, and may correct a genuine technical or typographic
          error before order acceptance. We will not cancel an accepted order merely
          because an offer was successful, and mandatory consumer rights remain intact.
        </p>
      </PolicySection>

      <PolicySection title="Returns">
        <p>
          Promotion purchases follow the <Link to="/returns">Returns &amp; Refunds Policy</Link>.
          A refund cannot exceed the amount actually paid for the returned item.
        </p>
      </PolicySection>
    </PolicyLayout>
  );
}

export function GiftCards() {
  return (
    <PolicyLayout
      title="Gift Cards & Store Credit Policy"
      intro="This policy applies only if mefurvexo offers a gift card, voucher, or store credit. The value, currency, validity, and any product-specific terms shown when it is issued also apply."
      contactSubject="a gift card, voucher, or store credit"
    >
      <PolicySection title="Use and balance">
        <p>
          A valid code may be applied to eligible online purchases in its stated
          currency. It is not a credit or debit card. Unless stated otherwise, more
          than one purchase may be made until the balance is used, and any excess
          order amount must be paid using an accepted payment method.
        </p>
      </PolicySection>

      <PolicySection title="Expiry and fees">
        <p>
          Any expiry date or inactivity fee will be disclosed before purchase or
          issue and will apply only where lawful. If none is disclosed, we will not
          impose one retrospectively. Statutory rules that provide a longer validity
          period take priority.
        </p>
      </PolicySection>

      <PolicySection title="Refunds and cash redemption">
        <p>
          Gift cards are not redeemable for cash except where law requires. A refund
          for a purchase paid with store value is normally returned to the same form
          of value. This does not limit a cash refund right that cannot be excluded.
        </p>
      </PolicySection>

      <PolicySection title="Loss, misuse, and transfer">
        <p>
          Treat a code like cash and share it only with the intended recipient. We
          can replace a lost code only if ownership and remaining balance can be
          verified. Codes obtained through fraud, unauthorised resale, or technical
          manipulation may be suspended while investigated, subject to legal rights.
        </p>
      </PolicySection>

      <PolicySection title="Promotional credit">
        <p>
          Complimentary or promotional credit may have narrower eligibility and an
          expiry disclosed when issued. Purchased gift value will not be converted
          into promotional value after purchase.
        </p>
      </PolicySection>
    </PolicyLayout>
  );
}

export function IntellectualProperty() {
  return (
    <PolicyLayout
      title="Intellectual Property & Takedown Policy"
      intro="mefurvexo respects copyright, trade mark, design, and other intellectual-property rights. This page explains permitted site use and how a rights owner can report a concern."
      contactSubject="intellectual property or a takedown notice"
    >
      <PolicySection title="Ownership and third-party rights">
        <p>
          The mefurvexo name and original store materials are owned by or used with
          permission of their respective rights holders. A reference to a third-party
          name, payment method, material standard, or service is descriptive and does
          not claim endorsement or ownership. All third-party rights remain with their
          owners.
        </p>
      </PolicySection>

      <PolicySection title="Permitted use">
        <p>
          You may browse the site and keep a personal copy of an order confirmation
          or policy. Without written permission, you may not reproduce, republish,
          sell, remove rights notices from, create derivative commercial material
          from, or use store content to train a commercial model where that use
          infringes applicable rights. Lawful quotation, review, and other statutory
          exceptions are not restricted.
        </p>
      </PolicySection>

      <PolicySection title="Reporting a rights concern">
        <p>A good-faith notice should include:</p>
        <ul>
          <li>the rights owner&apos;s name and authorised representative, if any;</li>
          <li>the protected work, mark, or design and the basis of the claim;</li>
          <li>the exact store URL or product identifying the material at issue;</li>
          <li>contact details and a statement that the information is accurate and the complaint is authorised; and</li>
          <li>supporting registration, publication, or ownership information where available.</li>
        </ul>
      </PolicySection>

      <PolicySection title="Review and response">
        <p>
          We will review a complete notice, may request clarification, and may remove
          or restrict material while a credible claim is assessed. Where appropriate,
          the person who supplied the material may be invited to respond. Knowingly
          false notices or counter-notices can cause harm; submit only accurate,
          good-faith claims. This process does not decide ownership or replace any
          legal remedy.
        </p>
      </PolicySection>

      <PolicySection title="Customer submissions">
        <p>
          Do not upload a photograph, review, logo, or text unless you created it or
          have permission to use it. The limited licence for an accepted submission
          is described in the <Link to="/terms">Terms of Service &amp; Sale</Link>.
        </p>
      </PolicySection>
    </PolicyLayout>
  );
}

export function Accessibility() {
  return (
    <PolicyLayout
      title="Accessibility Statement"
      intro="mefurvexo aims to make the online store usable by as many people as reasonably possible, including people who use keyboards, screen readers, zoom, or reduced-motion settings."
      contactSubject="an accessibility barrier or accommodation request"
    >
      <PolicySection title="Our approach">
        <p>
          We work toward clear headings, descriptive labels and alternative text,
          keyboard-operable controls, visible focus, readable contrast, responsive
          layouts, and plain-language customer information. This is a continuing
          effort rather than a claim that every page meets a particular standard.
        </p>
      </PolicySection>

      <PolicySection title="Third-party services">
        <p>
          Some payment, delivery, or embedded services are operated by other
          providers. We do not control every aspect of those interfaces, but welcome
          reports so we can offer a reasonable alternative or raise the issue with
          the provider where possible.
        </p>
      </PolicySection>

      <PolicySection title="Requesting help">
        <p>
          If a page, product detail, form, or checkout step is difficult to use,
          email the page URL, a short description of the barrier, and—only if you
          are comfortable—the device, browser, or assistive technology involved. Do
          not include medical information. We will try to provide the same information
          or service through an accessible alternative without extra charge.
        </p>
      </PolicySection>
    </PolicyLayout>
  );
}

export function Security() {
  return (
    <PolicyLayout
      title="Security & Fraud Prevention"
      intro="This policy explains steps used to protect the store and how customers can report suspicious activity. It does not disclose controls that would weaken security."
      contactSubject="security, fraud, or suspected phishing"
    >
      <PolicySection title="Store safeguards">
        <p>
          We use proportionate access, data-minimisation, provider, and operational
          safeguards. Complete payment-card details are entered on a hosted payment
          page rather than intentionally stored by this website. No system is immune
          from risk, so these measures are reviewed as the store changes.
        </p>
      </PolicySection>

      <PolicySection title="Order verification">
        <p>
          An unusual order may be paused for review. We may ask for limited evidence
          reasonably needed to confirm payment authority or delivery details. We do
          not ask by email for a complete card number, card security code, password,
          or payment-provider login. An order may be declined where risk cannot be
          resolved fairly.
        </p>
      </PolicySection>

      <PolicySection title="Protecting yourself">
        <ul>
          <li>Use the website address you trust and check the payment page before entering details.</li>
          <li>Do not send complete payment credentials by email or direct message.</li>
          <li>Keep devices and browsers updated and avoid payment over untrusted networks.</li>
          <li>Contact your payment provider immediately if a card or charge may be compromised.</li>
        </ul>
      </PolicySection>

      <PolicySection title="Reporting a concern">
        <p>
          Forward suspicious sender details, the page URL, approximate time, and a
          screenshot to our contact email. Remove passwords, full card numbers,
          identity documents, and unrelated personal data first. We will assess a
          credible report and take reasonable containment, notification, and remedial
          steps required by applicable law.
        </p>
      </PolicySection>
    </PolicyLayout>
  );
}

export function Complaints() {
  return (
    <PolicyLayout
      title="Complaints & Dispute Resolution"
      intro="If something went wrong, contact us so the facts and an appropriate remedy can be reviewed. This process is free and does not limit legal or payment-provider rights."
      contactSubject="a complaint or unresolved dispute"
    >
      <PolicySection title="1. How to complain">
        <p>
          Email the order number, the product or service involved, what happened,
          relevant dates, the outcome you are seeking, and supporting photographs or
          documents. Do not send complete card details or unnecessary identity data.
        </p>
      </PolicySection>

      <PolicySection title="2. Our review">
        <p>
          We aim to acknowledge a complaint within five business days and provide a
          substantive response within 15 business days. A complex carrier, payment,
          or technical investigation may take longer; if so, we will explain the
          reason and provide an update. These targets are service goals, not a waiver
          of any shorter statutory deadline.
        </p>
      </PolicySection>

      <PolicySection title="3. Escalation">
        <p>
          If the first response does not resolve the issue, reply with the word
          &quot;Escalation&quot; and explain what remains disputed. A person not responsible
          for the initial response will review it where practicable. Available
          outcomes depend on the facts and may include information, correction,
          repair, replacement, price adjustment, refund, or another lawful remedy.
        </p>
      </PolicySection>

      <PolicySection title="4. External options">
        <p>
          If a dispute remains unresolved, you may seek help from an applicable
          consumer-protection body, privacy regulator, payment provider, court, or
          other dispute-resolution service available where you live. We do not
          require confidential arbitration or waive a consumer forum through this
          policy. Please act within any legal time limit.
        </p>
      </PolicySection>

      <PolicySection title="5. Respectful communication">
        <p>
          We will communicate professionally and ask the same of customers. We may
          set reasonable boundaries on abusive, threatening, discriminatory, or
          persistently duplicative messages while continuing to address the substance
          of a legitimate complaint and making reasonable accessibility accommodations.
        </p>
      </PolicySection>
    </PolicyLayout>
  );
}
